import React from 'react';
import * as yup from 'yup';
import { statusColors } from 'utils/variables/statusColors';
import { createColumnHelper } from '@tanstack/react-table';

const HeaderWithTitle = ({ header, title, description }) => {
  return (
    <div
      title={`${title} - ${description}`}
      style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}
    >
      {header}
    </div>
  );
};

/**
 * Gera a configuração dos cards de estatísticas (Lado Esquerdo)
 */
export const getCardStatsConfig = (
  monitoringData,
  cardOrder,
  serviceLevelConfig
) => {
  const data = monitoringData || {};
  const serviceLevel = data.serviceLevel || {};

  const acceptablePercentage = serviceLevelConfig?.acceptablePercentage ?? 80;
  const acceptablePercentageNonConformities =
    serviceLevelConfig?.acceptablePercentageOfNonConformities ?? 20;

  const parsePercent = (val) => {
    if (typeof val === 'string') return parseFloat(val.replace('%', ''));
    return parseFloat(val || 0);
  };

  const serviceLevelValid = parsePercent(serviceLevel.accordingPercentage);
  const serviceLevelInvalid = parsePercent(serviceLevel.notAccordingPercentage);

  const attendanceLevelValue = data.answeredPercentage || '0%';

  const isValidBreached = serviceLevelValid < acceptablePercentage;
  const isInvalidBreached =
    serviceLevelInvalid > acceptablePercentageNonConformities;
  const isSlaBreached = isValidBreached || isInvalidBreached;

  const alertBackgroundColor = statusColors.FULLY_OCCUPIED;

  const cardDefinitions = [
    {
      id: 'TMA',
      title: 'Tempo médio de atendimento',
      layout: 'vertical-centered-single',
      value: data.TMA || '00:00:00'
    },
    {
      id: 'TME',
      title: 'Tempo médio de espera',
      layout: 'vertical-centered-single',
      value: data.TME || '00:00:00'
    },
    {
      id: 'TMO',
      title: 'Tempo médio operacional',
      layout: 'vertical-centered-single',
      value: data.TMO || '00:00:00'
    },
    {
      id: 'LONGEST_WAIT',
      title: 'Maior tempo de espera',
      layout: 'vertical-centered-single',
      value: data.longestWaitTime || '00:00:00'
    },
    {
      id: 'ANSWERED_PERCENTAGE',
      title: 'Nível de Atendimento (%)',
      layout: 'vertical-centered-single',
      value: data.answeredPercentage || '0%',
      description: 'Atendidas / Total'
    },
    {
      id: 'SERVICE_LEVEL',
      title: 'Nível de serviço (Qtd)',
      layout: 'vertical-centered-double',
      leftValue: {
        description: 'Conforme',
        value: serviceLevel.according || 0
      },
      rightValue: {
        description: 'Não conforme',
        value: serviceLevel.notAccording || 0
      }
    },
    {
      id: 'WAITING_QUEUE',
      title: 'Fila de espera',
      layout: 'vertical-centered-single',
      value: data.waitingList || 0
    },
    {
      id: 'ATTENDANCE_LEVEL_QTY',
      title: 'Nível de Atendimento (Qtd)',
      layout: 'vertical-centered-double',
      leftValue: {
        description: 'Conforme',
        value: data.qtdAnswered || 0
      },
      rightValue: {
        description: 'Não conforme',
        value: data.qtdAbandoned || 0
      }
    },
    {
      id: 'SERVICE_LEVEL_PERCENTAGE',
      title: 'Nível de serviço (%)',
      layout: 'vertical-centered-double',
      isAlert: isSlaBreached,
      backgroundColor: isSlaBreached ? alertBackgroundColor : undefined,
      leftValue: {
        description: 'Conforme',
        value: serviceLevel.accordingPercentage || '0%',
        suffix: ''
      },
      rightValue: {
        description: 'Não conforme',
        value: serviceLevel.notAccordingPercentage || '0%',
        suffix: ''
      }
    }
  ];

  return cardOrder
    .map((id) => cardDefinitions.find((c) => c.id === id))
    .filter(Boolean);
};

/**
 * Gera a configuração dos círculos do cabeçalho
 * Note: Telephony Monitoring handles "Agent Events" directly in the View via useAgentEvents.
 * pass counts directly here.
 */
export const getCircleStatsConfig = (
  agentsStatusCounts,
  activeFilters = []
) => {
  return [
    {
      id: 'available_agents',
      title: 'Disponíveis',
      value: agentsStatusCounts.available || 0,
      backgroundColor: statusColors.AVAILABLE,
      clickable: true,
      filterKey: 'IS_AVAILABLE',
      isActive: activeFilters.includes('IS_AVAILABLE')
    },
    {
      id: 'paused_agents',
      title: 'Em Pausa',
      value: agentsStatusCounts.paused || 0,
      backgroundColor: statusColors.PAUSE,
      clickable: true,
      filterKey: 'IS_PAUSED',
      isActive: activeFilters.includes('IS_PAUSED')
    },
    {
      id: 'active_paused_agents',
      title: 'Pausa Ativa',
      value: agentsStatusCounts.activePaused || 0,
      backgroundColor: statusColors.ACTIVE_PAUSE,
      clickable: true,
      filterKey: 'IS_ACTIVE_PAUSE',
      isActive: activeFilters.includes('IS_ACTIVE_PAUSE')
    },

    {
      title: 'Em Atendimento',
      value: agentsStatusCounts.talking || 0,
      backgroundColor: '#2ECC71',
      clickable: true,
      filterKey: 'IS_TALKING',
      isActive: activeFilters.includes('IS_TALKING')
    },
    {
      id: 'unregistered_agents',
      title: 'Offline: Logado mas sem fila ou registro',
      value: agentsStatusCounts.unregistered || 0,
      backgroundColor: statusColors.UNPLACED,
      clickable: true,
      filterKey: 'IS_UNREGISTERED',
      isActive: activeFilters.includes('IS_UNREGISTERED')
    },
    {
      id: 'offline_agents',
      title: 'Não logado: Sem sessão ativa na plataforma',
      value: agentsStatusCounts.offline || 0,
      backgroundColor: statusColors.OFFLINE,
      clickable: true,
      filterKey: 'IS_OFFLINE',
      isActive: activeFilters.includes('IS_OFFLINE')
    },
    {
      id: 'calls_in_wait',
      title: 'Fila de Espera',
      value: agentsStatusCounts.waitingCalls || 0,
      backgroundColor: '#E74C3C',
      clickable: false,
      filterKey: null
    },
    {
      id: 'total_agents',
      title: 'Total de usuários logados',
      value: agentsStatusCounts.total || 0,
      backgroundColor: '#5E72E4',
      clickable: false,
      filterKey: null,
      isActive: false
    }
  ];
};

const columnHelper = createColumnHelper();

export const matrixColumnsConfig = [
  columnHelper.accessor('interval', {
    header: 'Horário',
    cell: (info) => info.getValue(),
    size: 100
  }),
  columnHelper.accessor('qtdAnswered', {
    header: () => (
      <HeaderWithTitle
        header="Atendidas"
        title="Total de chamadas atendidas"
        description="Total de chamadas atendidas neste intervalo de horário"
      />
    ),
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor('qtdAbandoned', {
    header: () => (
      <HeaderWithTitle
        header="Abandonadas"
        title="Chamadas Abandonadas"
        description="Total de chamadas que desistiram na fila neste intervalo"
      />
    ),
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor('tme', {
    header: () => (
      <HeaderWithTitle
        header="TME"
        title="Tempo médio de espera"
        description="Tempo médio que os clientes esperaram na fila antes do atendimento"
      />
    ),
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor('tmo', {
    header: () => (
      <HeaderWithTitle
        header="TMO"
        title="Tempo médio operacional"
        description="Tempo médio operacional neste intervalo de horário"
      />
    ),
    cell: (info) => info.getValue()
  }),
   columnHelper.accessor('tma', {
    header: () => (
      <HeaderWithTitle
        header="TMA"
        title="Tempo médio de atendimento"
        description="Tempo médio de duração dos atendimentos (falados) neste intervalo"
      />
    ),
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor('tta', {
    header: () => (
      <HeaderWithTitle
        header="TTA"
        title="Tempo Total de Atendimento"
        description="Soma de todos os tempos de atendimento neste intervalo"
      />
    ),
    cell: (info) => info.getValue() || '00:00:00'
  }),
  columnHelper.accessor('answeredPercentage', {
    header: () => (
      <HeaderWithTitle
        header="Nível Atend. (%)"
        title="Nível de Atendimento"
        description="Percentual de chamadas atendidas sobre o volume total"
      />
    ),
    cell: (info) => {
      const val = info.getValue();
      if (val !== undefined && val !== null) return val;
      const row = info.row.original;
      const total = Number(row.qtdTotal || 0);
      const answered = Number(row.qtdAnswered || 0);
      return total > 0 ? ((answered / total) * 100).toFixed(1) + '%' : '0.0%';
    }
  }),
  columnHelper.accessor('abandonedPercentage', {
    header: () => (
      <HeaderWithTitle
        header="Abandono (%)"
        title="Taxa de Abandono"
        description="Percentual de chamadas abandonadas sobre o volume total"
      />
    ),
    cell: (info) => {
      const val = info.getValue();
      if (val !== undefined && val !== null) return val;
      const row = info.row.original;
      const total = Number(row.qtdTotal || 0);
      const abandoned = Number(row.qtdAbandoned || 0);
      return total > 0 ? ((abandoned / total) * 100).toFixed(1) + '%' : '0.0%';
    }
  }),
  columnHelper.accessor('serviceLevel', {
    header: () => (
      <HeaderWithTitle
        header="Nível de Serviço"
        title="Nível de Serviço"
        description="Percentual de chamadas atendidas dentro do tempo limite estipulado (SLA)"
      />
    ),
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor('projectedVolume', {
    header: () => (
      <HeaderWithTitle
        header="Vol. Projetado"
        title="Volume Projetado"
        description="Previsão de volume de chamadas para este intervalo"
      />
    ),
    cell: (info) => info.getValue()
  }),
  columnHelper.accessor('qtdTotal', {
    header: () => (
      <HeaderWithTitle
        header="Volume Total"
        title="Volume Total"
        description="Volume total de chamadas entrantes (Atendidas + Abandonadas)"
      />
    ),
    cell: (info) => info.getValue()
  })
];

export const getAgentsStatusCounts = (
  agents,
  watingCallsCount,
  offlineCount = 0
) => {
  const counts = {
    total: 0,
    available: 0,
    paused: 0,
    activePaused: 0,
    talking: 0,
    waitingCalls: watingCallsCount,
    unregistered: 0,
    offline: offlineCount
  };

  if (!Array.isArray(agents)) return counts;

  counts.total = agents.length;

  agents.forEach((agent) => {
    if (getIsAvailable(agent)) counts.available++;
    else if (getIsPaused(agent)) counts.paused++;
    else if (getIsActivePause(agent)) counts.activePaused++;
    else if (getIsTalking(agent)) counts.talking++;
    else if (getIsUnregistered(agent)) counts.unregistered++;
  });

  return counts;
};

export const getFilteredQueueMembers = (queueMemberInfo, activeFilters) => {
  if (!activeFilters || activeFilters.length === 0) return queueMemberInfo;

  return queueMemberInfo.filter((member) => {
    const state = member?.state || member?.agent?.state || '';

    const isAvailable = state === 'AVAILABLE';
    const isPaused = state === 'PAUSE_AUTO' || state === 'PAUSED';
    const isActivePause = state === 'PAUSE';
    const isTalking =
      state === 'RING' ||
      state === 'RINGING' ||
      state === 'INCALL' ||
      !!member?.channel ||
      !!member?.agent?.channel;
    const isUnregistered =
      state === 'UNAVAILABLE' || state === 'UNKNOWN' || state === 'UNPLACED';

    if (activeFilters.includes('IS_AVAILABLE') && isAvailable) return true;
    if (activeFilters.includes('IS_PAUSED') && isPaused) return true;
    if (activeFilters.includes('IS_ACTIVE_PAUSE') && isActivePause) return true;
    if (activeFilters.includes('IS_TALKING') && isTalking) return true;
    if (activeFilters.includes('IS_UNREGISTERED') && isUnregistered)
      return true;

    return false;
  });
};

const getIsAvailable = (a) => a.state === 'AVAILABLE';
const getIsPaused = (a) => a.state === 'PAUSE_AUTO' || a.state === 'PAUSED';
const getIsActivePause = (a) => a.state === 'PAUSE';
const getIsTalking = (a) =>
  a.state === 'RING' ||
  a.state === 'RINGING' ||
  a.state === 'INCALL' ||
  !!a.channel;
const getIsUnregistered = (a) =>
  a.state === 'UNAVAILABLE' || a.state === 'UNKNOWN' || a.state === 'UNPLACED';
